import { motion } from 'framer-motion';
import AnimatedAvatar from './AnimatedAvatar';
import FranceClosureMap from './FranceClosureMap';

/**
 * AuditRealizationSection — "Le moment de vérité" section avec carte France + bulle Sabril.
 * Affichée au début de l'audit pour créer l'effet "intrigue → choc → prise de conscience"
 * AVANT que le user voie ses chiffres détaillés.
 */
const AuditRealizationSection = ({ restoName, margePerdueParAn }) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7 }}
      className="relative overflow-hidden rounded-3xl my-6"
      style={{ background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 100%)' }}
      data-testid="audit-realization-section"
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 20% 30%, rgba(239,68,68,0.20) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(240,90,26,0.12) 0%, transparent 50%)',
        }}
        aria-hidden="true"
      />

      <div className="relative p-6 sm:p-8">
        {/* Sabril delivers the audit personally — humanise le moment */}
        <div className="mb-6 flex justify-center">
          <AnimatedAvatar
            size="md"
            position="right"
            bubbleArrow="left"
            delay={0.2}
            testid="audit-realization-avatar"
            bubbleClassName="!bg-white"
            message={[
              `${restoName ? restoName + ', s' : 'S'}alut, c'est Sabril 👋 Je viens de regarder ton audit avec toi.`,
              `Mon insight n°1 : 80% de ton temps doit aller sur la structure et le développement.`,
              `Si tu subis l'opérationnel, ce sera toujours compliqué — peu importe ton CA.`,
              `Avant d'aller voir tes chiffres, lis ça calmement. C'est le déclic.`,
            ]}
          />
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <p className="text-[10px] font-black uppercase tracking-widest text-red-400 mb-2">
            💡 Le moment où tout change
          </p>
          <h2 className="font-bebas text-3xl sm:text-4xl text-white leading-tight">
            Ces <span className="text-red-400">{margePerdueParAn.toLocaleString('fr-FR')}€</span> perdus<br/>
            cette année <span className="text-[#F05A1A]">— ce n'est pas le marché</span>
          </h2>
        </div>

        {/* Mini carte France — contexte visuel des fermetures */}
        <div className="mb-6 max-w-xs mx-auto" data-testid="audit-mini-france-map">
          <FranceClosureMap />
        </div>

        {/* Transition */}
        <div className="text-center mb-5">
          <p className="text-white/60 text-xs uppercase tracking-widest font-bold">
            Et la vraie raison derrière tous ces points rouges ?
          </p>
          <div className="inline-flex items-center gap-2 mt-2">
            <span className="h-px w-12 bg-red-500/50" />
            <span className="text-red-400 text-[10px] font-black uppercase tracking-widest">↓</span>
            <span className="h-px w-12 bg-red-500/50" />
          </div>
        </div>

        {/* Bulle de dialogue personnalisée */}
        <div className="bg-white rounded-2xl p-5 sm:p-6 mb-5 shadow-[0_12px_36px_rgba(0,0,0,0.4)] max-w-md mx-auto">
          <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mb-2 flex items-center gap-2">
            <span className="inline-flex w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            Prise de conscience
          </p>
          <p className="font-bebas text-4xl sm:text-5xl text-[#1a1a1a] leading-[0.9]">
            "C'est de<br/>
            <span className="text-red-500">ma faute.</span>"
          </p>
          <p className="text-[#1a1a1a]/65 text-xs italic mt-2">
            — La seule phrase qui peut tout débloquer.
          </p>
        </div>

        {/* Texte posé — apparition staggered au scroll */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={{
            hidden: { opacity: 1 },
            visible: { opacity: 1, transition: { staggerChildren: 0.45, delayChildren: 0.2 } },
          }}
          className="max-w-2xl mx-auto space-y-3 text-white/80 text-sm sm:text-[15px] leading-relaxed mb-6"
          data-testid="audit-realization-prose"
        >
          <motion.p
            variants={{
              hidden: { opacity: 0, y: 14, filter: 'blur(4px)' },
              visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.7, ease: 'easeOut' } },
            }}
          >
            <span className="text-white font-bold">
              {restoName ? `${restoName}, ` : ''}ces chiffres ne mentent pas.
            </span>{' '}
            Mais ce ne sont pas tes équipes, ni la concurrence, ni les charges.
          </motion.p>
          <motion.p
            variants={{
              hidden: { opacity: 0, y: 14, filter: 'blur(4px)' },
              visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.7, ease: 'easeOut' } },
            }}
          >
            C'est <span className="text-[#F05A1A] font-bold">toi qui n'as pas (encore) installé</span> le
            pilotage industriel qui transforme un resto qui survit en marque qui dure. Pas par
            manque de talent — par manque <span className="text-white font-bold">de cadre</span>.
            Tant que <span className="text-red-400 font-bold">80% de ton temps part dans l'opérationnel</span>{' '}
            au lieu d'aller sur la structure et le développement,{' '}
            <span className="text-white font-bold">tu subis</span> au lieu de piloter.
          </motion.p>
          <motion.p
            variants={{
              hidden: { opacity: 0, y: 14, filter: 'blur(4px)' },
              visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.7, ease: 'easeOut' } },
            }}
          >
            <span className="text-red-400 font-bold">Maintenant que tu sais ça,</span>{' '}
            on peut regarder tes chiffres ensemble — avec le bon prisme.
            Scrolle en dessous, ton diagnostic personnalisé t'attend.
          </motion.p>
        </motion.div>

        {/* CTA discret WhatsApp (au lieu des 2 chemins, car la conversion sera en bas de page) */}
        <div className="text-center">
          <a
            href={`https://wa.me/33745522450?text=${encodeURIComponent(`Salut Sabril, je viens de faire mon diagnostic${restoName ? ' pour ' + restoName : ''}. J'aimerais en parler avec toi.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-[#F05A1A] text-white/80 hover:text-white text-[11px] font-black uppercase tracking-wider px-4 py-2 rounded-full border border-white/20 hover:border-[#F05A1A] transition-colors"
            data-testid="audit-realization-cta"
          >
            💬 Tu peux aussi m'écrire dès maintenant →
          </a>
        </div>
      </div>
    </motion.section>
  );
};

export default AuditRealizationSection;
